import { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { Button } from '../../components/button';
import { Modal } from '../../components/modal';
import { Alert, Divider, LoadingBlock, Muted, Stack, Strong } from '../../components/ui';
import { useStripePromise } from '../../hooks/useStripePromise';
import {
  useConfirmStayModificationMutation,
  useModifyStayBookingMutation,
  useStayCalendarQuery,
  useStayModificationQuoteLazyQuery,
  type IStayBookingFieldsFragment,
} from '../../services/stayService/__generated__/queries.generated';
import { stayErrorMessage } from '../StayHostPage/stayErrors';
import { DatesModal, GuestsPicker } from '../StayGuestPage/StayGuestComponents';
import { formatMoney } from '../StayGuestPage/stayQuoteLines';
import { dateRangeLabel, guestSummary, type StayGuests } from '../StayGuestPage/staySearchState';
import { Detail } from './StayBookingComponents';
import { changeMoneyText, stayDayKeys } from './stayBooking';

/**
 * Change a confirmed stay's dates or guests (spec 016 B31, B32). The server prices the
 * change; a higher total is charged on the stay's card (a bank check runs here), a lower
 * one is refunded at the share the cancellation policy allows right now.
 */
export function ChangeStayModal({
  booking,
  isOpen,
  onClose,
  onChanged,
}: {
  booking: IStayBookingFieldsFragment;
  isOpen: boolean;
  onClose: () => void;
  onChanged: () => void;
}) {
  const zone = booking.car?.location?.timezone ?? 'UTC';
  const initial = useMemo(
    () => stayDayKeys(booking.startAt, booking.endAt, zone),
    [booking.startAt, booking.endAt, zone],
  );
  const initialGuests: StayGuests = {
    adults: booking.guestCounts?.adults ?? 1,
    children: booking.guestCounts?.children ?? 0,
    infants: booking.guestCounts?.infants ?? 0,
    pets: booking.guestCounts?.pets ?? 0,
  };
  const started = Date.now() >= booking.startAt;
  const [dates, setDates] = useState(initial);
  const [guests, setGuests] = useState<StayGuests>(initialGuests);
  const [datesOpen, setDatesOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    setDates(initial);
    setGuests(initialGuests);
    setError(null);
    // Reset only when the dialog opens.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const calendar = useStayCalendarQuery({
    variables: { id: booking.car?.id ?? '', excludeBookingId: booking.id },
    skip: !isOpen || !booking.car?.id,
  });
  const blocked = useMemo(
    () => new Set(calendar.data?.stayCalendar.blockedNights ?? []),
    [calendar.data],
  );

  const input = {
    bookingId: booking.id,
    checkIn: dates.checkIn,
    checkOut: dates.checkOut,
    guests,
  };
  const changed =
    dates.checkIn !== initial.checkIn ||
    dates.checkOut !== initial.checkOut ||
    JSON.stringify(guests) !== JSON.stringify(initialGuests);
  const [loadQuote, quote] = useStayModificationQuoteLazyQuery({ fetchPolicy: 'network-only' });
  useEffect(() => {
    if (isOpen && changed) void loadQuote({ variables: { input } });
    // The input is rebuilt from these values.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, changed, dates.checkIn, dates.checkOut, JSON.stringify(guests)]);

  const [modify] = useModifyStayBookingMutation();
  const [confirm] = useConfirmStayModificationMutation();
  const { stripePromise } = useStripePromise();

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      const { data } = await modify({ variables: { input } });
      let result = data?.modifyStayBooking;
      if (result?.status === 'REQUIRES_ACTION' && result.clientSecret && result.paymentIntentId) {
        const stripe = await stripePromise;
        if (!stripe) throw new Error('Payments are not ready yet. Please try again.');
        const bank = await stripe.confirmCardPayment(result.clientSecret);
        if (bank.error)
          throw new Error(bank.error.message || 'The bank did not approve this payment.');
        const confirmed = await confirm({
          variables: { input: { ...input, paymentIntentId: result.paymentIntentId } },
        });
        result = confirmed.data?.confirmStayModification;
      }
      if (result?.status !== 'SUCCEEDED') {
        setError(result?.errorMessage || 'The change could not be made.');
        return;
      }
      toast.success('Your reservation was changed');
      onChanged();
    } catch (e) {
      setError(stayErrorMessage(e, 'The change could not be made.'));
    } finally {
      setSaving(false);
    }
  };

  const answer = quote.data?.stayModificationQuote;
  const quoteError = changed && quote.error ? stayErrorMessage(quote.error) : null;
  const stayDetails = booking.car?.stayDetails;

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} title="Change reservation" modalWidth="480px">
        <Stack $gap={4} data-testid="stay-change-modal">
          <Strong style={{ fontSize: 20 }}>Change reservation</Strong>
          {started ? (
            <Muted>Your stay has started: you can stay longer or add guests.</Muted>
          ) : null}
          <Stack
            $gap={4}
            style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}
          >
            <Detail label="Dates">{dateRangeLabel(dates.checkIn, dates.checkOut)}</Detail>
            <Button
              theme="secondary"
              size="sm"
              text="Edit"
              onClick={() => setDatesOpen(true)}
              dataTestId="stay-change-dates"
            />
          </Stack>
          <div>
            <Strong>Guests</Strong>
            <Muted $size="sm" style={{ display: 'block', marginBottom: 8 }}>
              {guestSummary(guests)}
            </Muted>
            <GuestsPicker
              value={guests}
              maxGuests={stayDetails?.maxGuests ?? 16}
              petsAllowed={Boolean(stayDetails?.houseRules?.petsAllowed)}
              onChange={setGuests}
            />
          </div>
          <Divider />
          {!changed ? (
            <Muted>Pick new dates or guests to see the price.</Muted>
          ) : quote.loading && !answer ? (
            <LoadingBlock label="Pricing the change" />
          ) : quoteError ? (
            <Alert tone="warning" title="Cannot change">
              {quoteError}
            </Alert>
          ) : answer ? (
            <Stack $gap={4} data-testid="stay-change-quote">
              <Detail label="New total">
                {formatMoney(answer.newTotal)} (was {formatMoney(answer.oldTotal)})
              </Detail>
              <Strong data-testid="stay-change-money">{changeMoneyText(answer)}</Strong>
            </Stack>
          ) : null}
          {error ? (
            <Alert tone="error" title="Not changed">
              {error}
            </Alert>
          ) : null}
          <Button
            text="Confirm change"
            fullWidth
            isLoading={saving}
            isDisabled={!changed || !answer || Boolean(quoteError) || quote.loading}
            onClick={save}
            dataTestId="stay-change-confirm"
          />
          <Button theme="text" text="Keep my reservation" fullWidth onClick={onClose} />
        </Stack>
      </Modal>
      <DatesModal
        open={datesOpen}
        onClose={() => setDatesOpen(false)}
        checkIn={dates.checkIn}
        checkOut={dates.checkOut}
        blocked={blocked}
        minNights={calendar.data?.stayCalendar.minimumNights}
        maxNights={calendar.data?.stayCalendar.maximumNights}
        timezone={zone}
        lockedStart={started}
        onApply={(checkIn, checkOut) => {
          if (checkIn && checkOut) setDates({ checkIn, checkOut });
        }}
      />
    </>
  );
}
